<template>
  <div class="admin-bulk-upload-tab">
    <Typography variant="h-5" class="text-left">Create Bulk Upload</Typography>
    <Button iconLeft="mdi-plus" :loading="loadingForms" @click="openBulkUploadDialog"
      >Bulk Upload</Button
    >
    <v-divider class="align-self-stretch" />
    <v-progress-circular v-if="loadingBulkSubmissions" indeterminate color="primary" />
    <template v-else>
      <Typography v-if="totalBulkSubmissions === 0" variant="body-1">
        There are no bulk uploads in progress.
      </Typography>
      <div v-else class="bulk-submissions">
        <div>
          <Typography variant="h-5">Bulk Uploads in Progress</Typography>
          <Typography variant="body-1">
            If you save errors for later, they will appear here
          </Typography>
        </div>
        <BulkSubmissions
          :submissions="bulkSubmissions"
          :options="tableOptions"
          :total="totalBulkSubmissions"
          :loading="loadingBulkSubmissions"
          :footer-props="footerProps"
          @update:options="onTableOptionsChange"
          @edit-submission="editSubmission"
          @delete-submission="deleteSubmissionClicked"
        />
      </div>
    </template>
    <BulkUploadDialog
      :show="showBulkUploadDialog"
      @update:show="showBulkUploadDialog = $event"
      :forms="forms"
      @open-edit-submission="onOpenEditSubmission"
      @bulk-upload-submitted="fetchBulkClaimSubmissions"
      :search-loading="searchingForms"
      @search-forms="onSearchForms"
    />
    <ErrorDialogV2
      :value="showNoFormsDialog"
      @input="showNoFormsDialog = $event"
      title="You can't upload bulk claims yet"
      message="There is no claim form. Create a form to use this feature."
    />
    <EditBulkSubmissionDialog
      :show="showEditBulkSubmissionDialog"
      @update:show="showEditBulkSubmissionDialog = $event"
      :bulkSubmission="selectedBulkSubmission"
      @bulk-submission-submitted="onBulkSubmissionSubmitted"
    />
    <Modal
      :value="showDeleteBulkUploadModal"
      @input="showDeleteBulkUploadModal = $event"
      :show-close-button="false"
      :onClose="onCancelDelete"
      data-testid="delete-bulk-upload-modal"
    >
      <template #header>
        <Typography variant="h-5">Delete bulk upload?</Typography>
      </template>
      <Typography variant="body-1" class="text-left black--text pt-4">
        You'll lose {{ remainingErrorsCount }} remaining
        {{ remainingErrorsCount === 1 ? "error" : "errors" }}. This action cannot be undone.
      </Typography>
      <div class="d-flex justify-end pt-4">
        <Button text @click="onCancelDelete" :disabled="deletingBulkUpload">Cancel</Button>
        <Button text @click="deleteBulkUpload" :loading="deletingBulkUpload"
          >Delete Bulk Upload</Button
        >
      </div>
    </Modal>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import Modal from "@/components/generic/Modal.vue";
import BulkUploadDialog from "@/components/program/admin/bulk-upload/BulkUploadDialog/BulkUploadDialog.vue";
import ErrorDialogV2 from "@/components/ErrorDialogV2.vue";
import FormService from "@/services/FormService.js";
import { mapState } from "vuex";
import { debounce } from "@/shared_data/functions";
import BulkSubmissions from "./BulkSubmissions.vue";
import EditBulkSubmissionDialog from "./EditBulkSubmissionDialog.vue";
export default {
  name: "AdminBulkUploadTab",
  components: {
    Typography,
    Button,
    Modal,
    BulkUploadDialog,
    ErrorDialogV2,
    BulkSubmissions,
    EditBulkSubmissionDialog,
  },
  computed: {
    ...mapState(["globalClientId"]),
    remainingErrorsCount() {
      return this.selectedBulkSubmission?.BulkClaims?.length ?? 0;
    },
  },
  data() {
    return {
      bulkSubmissions: [],
      selectedBulkSubmission: null,
      loadingBulkSubmissions: false,
      deletingBulkUpload: false,
      totalBulkSubmissions: 0,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      footerProps: {
        "items-per-page-options": [5, 10, 25, 50],
      },
      showBulkUploadDialog: false,
      showNoFormsDialog: false,
      showEditBulkSubmissionDialog: false,
      showDeleteBulkUploadModal: false,
      forms: [],
      loadingForms: false,
      searchingForms: false,
      selectedForm: null,
    };
  },
  created() {
    this.debouncedSearchForms = debounce(this.searchForms, 300);
  },

  methods: {
    async fetchForms(search) {
      const response = await FormService.getFormsV2(undefined, this.globalClientId, {
        limit: 100,
        search: search || undefined,
      });
      this.forms = response?.result?.rows || [];
    },
    async openBulkUploadDialog() {
      this.loadingForms = true;
      try {
        await this.fetchForms();
      } catch (error) {
        this.$store.dispatch("showError", {
          message: "Error fetching forms",
        });
      } finally {
        this.loadingForms = false;
        if (this.forms.length > 0) {
          this.showBulkUploadDialog = true;
        } else {
          this.showNoFormsDialog = true;
        }
      }
    },
    onSearchForms(search) {
      this.debouncedSearchForms(search);
    },
    async searchForms(search) {
      this.searchingForms = true;
      try {
        await this.fetchForms(search);
      } catch (error) {
        this.$store.dispatch("showError", {
          message: "Error searching forms",
        });
      } finally {
        this.searchingForms = false;
      }
    },
    async fetchBulkClaimSubmissions() {
      this.loadingBulkSubmissions = true;
      try {
        const { page, itemsPerPage } = this.tableOptions;
        const offset = (page - 1) * itemsPerPage;
        const limit = itemsPerPage;

        const response = await FormService.getBulkClaimSubmissions({
          clientId: this.globalClientId,
          bulkClaimStatus: "Invalid",
          offset,
          limit,
        });
        this.bulkSubmissions = response?.result?.rows || [];
        this.totalBulkSubmissions = response?.result?.count || 0;
      } catch (error) {
        this.$store.dispatch("showError", {
          message: "Error fetching bulk claim submissions",
        });
      } finally {
        this.loadingBulkSubmissions = false;
      }
    },
    async editSubmission(submission) {
      await this.loadAndOpenEditSubmission(submission?.id);
    },
    async onBulkSubmissionSubmitted(bulkSubmissionId) {
      if (!bulkSubmissionId) return;
      try {
        const data = await FormService.getBulkClaimSubmission(bulkSubmissionId);
        const submission = data?.result ?? data;
        if (submission) {
          this.assignSelectedBulkSubmissionFromFetch(submission);
        }
      } catch (error) {
        this.$store.dispatch("showError", {
          message: "Error loading bulk submission",
        });
      }
    },
    async onOpenEditSubmission({ bulkClaimSubmissionId }) {
      this.showBulkUploadDialog = false;
      await this.loadAndOpenEditSubmission(bulkClaimSubmissionId);
    },
    assignSelectedBulkSubmissionFromFetch(submission) {
      if (!submission) return;
      this.selectedBulkSubmission = {
        ...submission,
        BulkClaims: (submission.BulkClaims || []).filter((claim) => claim.status === "Invalid"),
      };
    },
    async loadAndOpenEditSubmission(bulkClaimSubmissionId) {
      if (!bulkClaimSubmissionId) return;
      try {
        const data = await FormService.getBulkClaimSubmission(bulkClaimSubmissionId);
        const submission = data?.result ?? data;
        if (!submission) return;
        this.assignSelectedBulkSubmissionFromFetch(submission);
        this.showEditBulkSubmissionDialog = true;
      } catch (error) {
        this.$store.dispatch("showError", {
          message: "Error loading bulk submission",
        });
      }
    },
    onTableOptionsChange(newOptions) {
      const { page, itemsPerPage } = newOptions;
      // Only fetch if page or itemsPerPage actually changed
      if (this.tableOptions.page !== page || this.tableOptions.itemsPerPage !== itemsPerPage) {
        this.tableOptions = { ...newOptions };
        this.fetchBulkClaimSubmissions();
      }
    },
    deleteSubmissionClicked(submission) {
      this.selectedBulkSubmission = submission;
      this.showDeleteBulkUploadModal = true;
    },
    onCancelDelete() {
      this.showDeleteBulkUploadModal = false;
      this.selectedBulkSubmission = null;
    },
    async deleteBulkUpload() {
      if (!this.selectedBulkSubmission?.id) return;
      try {
        this.deletingBulkUpload = true;
        await FormService.updateBulkClaimSubmissionStatus({
          id: this.selectedBulkSubmission.id,
          status: "Deleted",
        });
        this.showDeleteBulkUploadModal = false;
        this.selectedBulkSubmission = null;
        await this.fetchBulkClaimSubmissions();
      } catch (error) {
        this.$store.dispatch("showError", {
          message: "Error deleting bulk upload",
        });
      } finally {
        this.deletingBulkUpload = false;
      }
    },
  },
  mounted() {
    this.fetchBulkClaimSubmissions();
  },
  watch: {
    globalClientId() {
      this.tableOptions = {
        page: 1,
        itemsPerPage: 10,
      };
      this.fetchBulkClaimSubmissions();
    },
    showEditBulkSubmissionDialog() {
      if (!this.showEditBulkSubmissionDialog) {
        this.fetchBulkClaimSubmissions();
      }
    },
  },
};
</script>

<style scoped>
.admin-bulk-upload-tab {
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1rem;
  align-items: flex-start;
  gap: 1.5rem;
}

.bulk-submissions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: left;
}
</style>
